// LocatPage.tsx
import React, { FC, useCallback, useEffect, useState } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import {
  IBranchDropSetting,
  ILocationModel,
  initialLocation,
  LOCATION_TYPE,
  MEETING_POINTS,
  tabOptions,
  validationLocation,
  WORKING_TIMES,
  YES_OR_NO,
} from './LocationModel'
import { createLocation, deleteLocation, loadLocations, updateLocation } from './Api'
import { ConfirmModal } from '../../../../shared/components/ConfirmModal'
import { ToastContainer } from 'react-toastify'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import PhoneInput from '../../../../shared/components/PhoneInput'
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import { LocationTable } from './LocationTable'
import { BranchDropTab } from './BranchDropTab'
import { DropDistancesPanel } from './DropDistancesPanel'
import { COUNTRY_OPTIONS, VISIBLE_OR_NOT } from '../../../../shared/models/SharedConstantLookups'
import { branches } from '../../../../shared/models/SharedApiLookups'

export const LocationPage: FC = () => {
  const [tab, setTab] = useState<string | 'form'>('form')
  const [locations, setLocations] = useState<ILocationModel[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [showModal, setShowModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize, setPageSize] = useState(10)
  const [activeDropBranch, setActiveDropBranch] = useState<IBranchDropSetting | null>(null)
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([])
  useEffect(() => {
    Promise.all([branches()]).then(([br]) => {
      setBranchOptions(br)
    })
  }, [])

  const fetchLocations = useCallback(async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
    try {
      const res = await loadLocations(page, pageSize, sort, order)
      const data = res.data.payload || res.data
      setLocations(data.content || data || [])
      setTotalPages(data.totalPages || 1)
    } catch (err) {
      const error = err as any
      if (error.response?.status === 401) {
        window.location.href = '/login'
      }
      console.error(error)
      showErrorToast('Lokasyonlar yüklenirken bir hata oluştu')
    }
  }, [])

  useEffect(() => {
    fetchLocations(currentPage)
  }, [fetchLocations, currentPage])

  const handleSave = async (values: ILocationModel, resetForm: () => void) => {
    setLoading(true)
    try {
      // Sadece kullanıcının çıkış şubesi olarak seçtiği veya en az bir alanı doldurduğu
      // şube ayarlarını gönder
      const branchDropSettings = (values.branchDropSettings || []).filter((s) => {
        return (
          s.isExitBranch ||
          (s.greetingType && s.greetingType !== '') ||
          (s.workingHoursType && s.workingHoursType !== '') ||
          (s.specialMail && s.specialMail.trim() !== '') ||
          (s.specialPhone && s.specialPhone.trim() !== '')
        )
      })

      // Sadece kullanıcı tarafından girilmiş drop bedeli/süre satırlarını gönder
      const dropDistances = (values.dropDistances || []).filter(
        (d) =>
          d.dropFee !== -1 ||
          d.dropFee2 !== -1 ||
          (d.supplyDuration && d.supplyDuration > 0) ||
          (d.minRental && d.minRental > 0)
      )

      const payloadValues: ILocationModel = {
        ...values,
        branchDropSettings,
        dropDistances,
      }

      if (selectedId) {
        await updateLocation({...payloadValues, id: selectedId})
        showSuccessToast('Lokasyon başarıyla güncellendi')
      } else {
        // Yeni kayıt: formdaki placeholder id ('0' / '') backend'de mevcut kaydı güncelliyordu
        const {id: _id, ...payload} = payloadValues
        await createLocation(payload as ILocationModel)
        showSuccessToast('Lokasyon başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchLocations(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (loc: ILocationModel, formik: FormikProps<ILocationModel>) => {
    formik.setValues(loc)
    setSelectedId(loc.id)
    setTab('form')
  }

  const handleDeleteClick = useCallback((location: ILocationModel) => {
    setSelectedId(location.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      try {
        await deleteLocation(selectedId)
        showSuccessToast('Lokasyon başarıyla silindi')
        await fetchLocations(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Lokasyon silinirken bir hata oluştu')
      } finally {
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchLocations(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchLocations]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchLocations(currentPage, column, order)
    },
    [currentPage, fetchLocations]
  )

  return (
    <>
      <Formik
        initialValues={initialLocation}
        validationSchema={validationLocation}
        enableReinitialize
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='locationForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='locationForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput name='name' label='Lokasyon Adı' wrapperClassName='col-md-6' />
                      <TextInput
                        name='nameEnglish'
                        label='Lokasyon Adı (İngilizce)'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <PhoneInput
                        name='phone'
                        type='phone'
                        label='Telefon'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='mailAddress'
                        type='email'
                        label='Mail Adresi'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='locationType'
                        label='Lokasyon Türü'
                        wrapperClassName='col-md-6'
                        options={LOCATION_TYPE}
                      />
                      <SelectInput
                        name='country'
                        label='Ülke'
                        wrapperClassName='col-md-6'
                        options={COUNTRY_OPTIONS}
                      />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='meetingPoint'
                        label='Buluşma Noktası'
                        wrapperClassName='col-md-6'
                        options={MEETING_POINTS}
                      />
                      <TextInput name='city' label='Şehir' wrapperClassName='col-md-3' />
                      <TextInput name='district' label='İlçe' wrapperClassName='col-md-3' />
                    </div>
                    <div className='row mb-6'>
                      <SelectInput
                        name='xmlServices'
                        label='XML Servislerde'
                        wrapperClassName='col-md-6'
                        options={VISIBLE_OR_NOT}
                      />
                      <TextInput
                        name='mapsLocation'
                        label='Maps Konum'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                    <div className='row mb-6'>
                      <TextInput name='iata' label='IATA' wrapperClassName='col-md-6' />

                      <TextInput
                        name='displayOrder'
                        type='number'
                        label='Sıralama'
                        wrapperClassName='col-md-3'
                      />
                      <SelectInput
                        name='defaultBranch'
                        label='Default Şube'
                        wrapperClassName='col-md-3'
                        options={branchOptions}
                      />
                    </div>

                    <div className='row mb-6'>
                      <TextAreaInput
                        name='address'
                        label='Lokasyon Adresi'
                        wrapperClassName='col-md-6'
                      />
                      <TextAreaInput
                        name='additionalDescription'
                        label='Ek Açıklama'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    {/* Çalışma Saatleri */}
                    <div className='mt-10'>
                      <h5 className='mb-4'>Lokasyonun Çalışma Saatlerini Giriniz</h5>
                      {WORKING_TIMES.map(({ label, startKey, endKey }) => (
                        <div className='row mb-3' key={label}>
                          <div className='col-md-3'>
                            <strong>{label}</strong>
                          </div>
                          <TextInput name={startKey} type='time' wrapperClassName='col-md-4' />
                          <TextInput name={endKey} type='time' wrapperClassName='col-md-4' />
                        </div>
                      ))}
                    </div>
                  </Form>
                </div>
              )}

              {tab === 'branchDrop' && !activeDropBranch && (
                <BranchDropTab
                  onOpenDropDistances={(branch) => setActiveDropBranch(branch)}
                />
              )}

              {tab === 'branchDrop' && activeDropBranch && (
                <DropDistancesPanel
                  branch={activeDropBranch}
                  currentLocationId={selectedId ?? formik.values.id}
                  currentLocationName={formik.values.name}
                  onBack={() => setActiveDropBranch(null)}
                />
              )}

              {tab === 'fileArticle' && (
                <div className='card mb-5'>
                  <div className='card-body text-center text-muted py-15'>
                    <i className='bi bi-file-earmark-text fs-2x d-block mb-3'></i>
                    Dosya & Makale Ekle ekranı yakında eklenecektir.
                  </div>
                </div>
              )}

              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body'>
                    <LocationTable
                      data={locations}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(branch) => handleEdit(branch, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu lokasyonu silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
